import { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import RadioGroup from '@mui/material/RadioGroup';
import Radio from '@mui/material/Radio';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import CancelIcon from '@mui/icons-material/Cancel';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTranslation } from 'react-i18next';

import { convertToLonLat } from '../utils/convertToLonLat';
import { JGD2011_ZONES, DEFAULT_JGD2011_EPSG } from '../utils/jgd2011Zones.js';
import { sampleGeoidN } from '../utils/sampleGeoidCog';
import { getPresignedRasterURLS3 } from '../utils/interfaceS3';
import config from '../config.js';

/**
 * Japan bounding box (approximate)
 */
const JAPAN_BOUNDS = {
  minLat: 24,
  maxLat: 46,
  minLon: 122,
  maxLon: 154,
};

/**
 * Check if coordinates are within Japan bounds
 */
const isInJapanBounds = (lon, lat) => {
  return (
    lat >= JAPAN_BOUNDS.minLat &&
    lat <= JAPAN_BOUNDS.maxLat &&
    lon >= JAPAN_BOUNDS.minLon &&
    lon <= JAPAN_BOUNDS.maxLon
  );
};

/**
 * Dialog component for selecting EPSG code and displaying Z translation (geoid undulation).
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.onClose - Callback when dialog is closed without confirmation
 * @param {Function} props.onConfirm - Callback when user confirms selection
 * @param {Object} props.lazHeader - LAZ header information from readLazHeaderInfo()
 * @param {string} props.idToken - AWS Cognito ID token for S3 access
 * @param {string} props.region - AWS region
 * @param {string} props.idpool - Cognito identity pool ID
 */
export default function EpsgSelectionDialog({
  open,
  onClose,
  onConfirm,
  lazHeader,
  idToken,
  region,
  idpool,
}) {
  const { t } = useTranslation();

  // NewDataDialog mounts a fresh session with a complete header. Initialize
  // before the first effect so the default zone never starts a competing request.
  const [heightType, setHeightType] = useState('orthometric');
  const [selectedEpsg, setSelectedEpsg] = useState(() => (
    JGD2011_ZONES.some(zone => zone.code === lazHeader?.epsg)
      ? lazHeader.epsg
      : DEFAULT_JGD2011_EPSG
  ));
  const [calculation, setCalculation] = useState(null);

  // Results belong to the inputs that produced them, including during the
  // render before an effect runs. Stale results cannot enable confirmation.
  const currentCalculation = calculation?.header === lazHeader &&
    calculation?.epsg === selectedEpsg && calculation?.heightType === heightType
    ? calculation
    : null;
  const zTranslation = heightType === 'ellipsoid' ? 0 : currentCalculation?.zTranslation ?? null;
  const lonLat = currentCalculation?.lonLat ?? null;
  const isInJapan = currentCalculation?.isInJapan ?? true;
  const geoidSource = currentCalculation?.geoidSource ?? 'japan';
  const calculationError = currentCalculation?.error ?? null;
  const isCalculating = open && heightType === 'orthometric' &&
    (!currentCalculation || currentCalculation.status === 'loading');

  useEffect(() => {
    if (!open || heightType !== 'orthometric') return;

    let active = true;
    const inputs = { header: lazHeader, epsg: selectedEpsg, heightType };
    setCalculation({ ...inputs, status: 'loading' });

    const calculate = async () => {
      let location = {};
      try {
        if (!lazHeader?.center) {
          throw new Error(t('ErrorMissingHeader'));
        }

        // A Japanese zone can be corrected via the dropdown. Other CRSs use
        // the file's detected EPSG and WKT rather than the dropdown default.
        const headerIsJGD2011 = JGD2011_ZONES.some(zone => zone.code === lazHeader.epsg);
        const result = convertToLonLat({
          x: lazHeader.center[0],
          y: lazHeader.center[1],
          epsg: headerIsJGD2011 ? selectedEpsg : lazHeader.epsg,
          wkt: lazHeader.wkt,
        });
        if (!result.ok) throw new Error(result.error.message);

        const { lon, lat } = result;
        const inJapan = isInJapanBounds(lon, lat);
        location = {
          lonLat: { lon, lat },
          isInJapan: inJapan,
          geoidSource: inJapan ? 'japan' : 'world',
        };
        setCalculation({ ...inputs, ...location, status: 'loading' });

        const rasterFilename = inJapan ? config.geoidRasterJapan : config.geoidRasterWorld;
        const urlGeoid = await getPresignedRasterURLS3(
          idToken,
          region,
          idpool,
          `${rasterFilename}`,
          86400
        );
        if (!active) return;

        const geoidResult = await sampleGeoidN(urlGeoid, lon, lat);
        if (!active) return;
        if (!geoidResult.ok) throw new Error(geoidResult.error.message);

        setCalculation({
          ...inputs,
          ...location,
          status: 'success',
          zTranslation: geoidResult.N,
        });
      } catch (err) {
        if (active) {
          setCalculation({
            ...inputs,
            ...location,
            status: 'error',
            error: err.message || String(err),
          });
        }
      }
    };

    calculate();
    // Ignore every obsolete response, including errors. It must not replace
    // the current result or stop its loading indicator after a change/close.
    return () => { active = false; };
  }, [open, lazHeader, selectedEpsg, heightType, idToken, region, idpool, t]);

  const handleHeightTypeChange = (event) => {
    if (event.target.value === heightType) return;
    setCalculation(null);
    setHeightType(event.target.value);
  };

  const handleEpsgChange = (event) => {
    if (event.target.value === selectedEpsg) return;
    setCalculation(null);
    setSelectedEpsg(event.target.value);
  };

  const canConfirm = () => open && (
    heightType === 'ellipsoid' || currentCalculation?.status === 'success'
  );

  const handleConfirm = () => {
    if (!canConfirm()) return;
    onConfirm({
      epsg: selectedEpsg,
      zTranslation,
      lonLat,
      heightType,
    });
  };

  const handleClose = () => {
    onClose();
  };

  // Find detected EPSG info for display
  const detectedEpsg = lazHeader?.epsg;
  const hasDetectedEpsg = detectedEpsg != null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <Box sx={{ p: 3 }}>
        <DialogTitle>
          <Typography variant="h5" component="div">
            {t('SelectCoordinateSystem')}
          </Typography>
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={(theme) => ({
            position: 'absolute',
            right: 16,
            top: 16,
            color: theme.palette.grey[500],
          })}
        >
          <CancelIcon />
        </IconButton>

        <DialogContent>
          <Stack spacing={3}>
              <Alert
                severity="info"
                icon={<InfoOutlinedIcon />}
                sx={{ '& .MuiAlert-message': { width: '100%' } }}
              >
                <Typography variant="body2">
                  {t('EpsgDialogExplanation')}
                </Typography>
              </Alert>
            <Box>
              {/* <Typography variant="subtitle2" gutterBottom>
                {t('HeightType')}
              </Typography> */}
              <FormControl component="fieldset">
                <RadioGroup
                  row
                  value={heightType}
                  onChange={handleHeightTypeChange}
                >
                  <FormControlLabel
                    value="orthometric"
                    control={<Radio />}
                    label={t('OrthometricHeight')}
                  />
                  <FormControlLabel
                    value="ellipsoid"
                    control={<Radio />}
                    label={t('EllipsoidHeight')}
                  />
                </RadioGroup>
              </FormControl>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                {heightType === 'ellipsoid'
                  ? t('EllipsoidHeightDescription')
                  : t('OrthometricHeightDescription')}
              </Typography>
            </Box>

            {/* Orthometric-specific options */}
            {heightType === 'orthometric' && (
              <>
                {/* Detected EPSG info */}
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t('DetectedEPSG')}:{' '}
                    {hasDetectedEpsg ? (
                      <strong>EPSG:{detectedEpsg}</strong>
                    ) : (
                      <em>{t('NotDetected')}</em>
                    )}
                  </Typography>
                </Box>

                {/* EPSG Selection Dropdown */}
                {isInJapan && <FormControl fullWidth>
                  <InputLabel id="epsg-select-label">{t('CoordinateSystem')}</InputLabel>
                  <Select
                    labelId="epsg-select-label"
                    id="epsg-select"
                    value={selectedEpsg}
                    label={t('CoordinateSystem')}
                    onChange={handleEpsgChange}
                  >
                    {JGD2011_ZONES.map((zone) => (
                      <MenuItem key={zone.code} value={zone.code}>
                        <Box>
                          <Typography variant="body2">
                            EPSG:{zone.code} - {t('Zone')} {zone.label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t(zone.regionKey)}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>}

                {/* Location info */}
                {/* {lonLat && (
                  <Typography variant="caption" color="text.secondary">
                    {t('DatasetLocation')}: {lonLat.lat.toFixed(4)}°N, {lonLat.lon.toFixed(4)}°E
                  </Typography>
                )} */}

                {/* Z Translation Result */}
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 1,
                    bgcolor: 'background.default',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="subtitle2" gutterBottom>
                    {t('EstimatedZTranslation')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    {t('ZTranslationExplanation')}
                  </Typography>

                  {isCalculating && (
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                      <CircularProgress size={20} />
                      <Typography variant="body2" color="text.secondary">
                        {geoidSource === 'japan' ? t('FetchingGeoidJapan') : t('FetchingGeoidWorld')}
                      </Typography>
                    </Stack>
                  )}

                  {calculationError && !isCalculating && (
                    <Alert severity="error" sx={{ mt: 1 }}>
                      {calculationError}
                    </Alert>
                  )}

                  {zTranslation !== null && !isCalculating && !calculationError && (
                    <Stack spacing={1}>
                      <Typography variant="h6" color="text.secondary">
                        {zTranslation.toFixed(3)} m
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {geoidSource === 'japan' ? t('GeoidSourceJapan') : t('GeoidSourceWorld')}
                      </Typography>
                    </Stack>
                  )}
                </Box>
              </>
            )}

            {/* Ellipsoid mode info */}
            {/* {heightType === 'ellipsoid' && (
              <Box
                sx={{
                  p: 2,
                  borderRadius: 1,
                  bgcolor: 'background.default',
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography variant="subtitle2" gutterBottom>
                  {t('ZTranslation')}
                </Typography>
                <Typography variant="h6" color="text.secondary">
                  0 m
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('EllipsoidNoTranslation')}
                </Typography>
              </Box>
            )} */}
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} color="inherit">
            {t('Cancel')}
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            disabled={!canConfirm()}
          >
            {t('Confirm')}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
